import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getCampaigns } from '../../../actions/campains'
import { campaignTemplateToText } from '../../../features/ManageCampaigns/CampaignCard/templateToText'
import { useCampaignName } from '../../../features/ManageCampaigns/CampaignCard/useCampaignName'
import ConfirmationDialog from '../ConfirmationDialog'
import LoadingIndicator from '../LoadingIndicator'
import styles from './CompareCampaigns.module.scss'

const CampaignCard = ({ category, messageTemplate }) => {
  const [campaignName] = useCampaignName(category)

  return (
    <div className={styles.card}>
      <h3>{campaignName}</h3>
      <div>{campaignTemplateToText(messageTemplate, 'Sinan', 'Hotel X')}</div>
    </div>
  )
}

const CompareCampaigns = ({
  originSubsidiaryId,
  originSubsidiaryName,
  destinationSubsidiaryId,
  destinationSubsidiaryName,
  handleClose,
  handleConfirm
}) => {
  const dispatch = useDispatch()

  const campaigns = useSelector(s => s.campaigns.content)

  const allOriginContent = useSelector(s => s.campaigns.content[originSubsidiaryId])

  const allDestinationContent = useSelector(
    s => s.campaigns.content[destinationSubsidiaryId]
  )

  const originContent = allOriginContent
    ?.filter(c => c.kind === 'Sales')
    ?.filter(c => c.category !== 'Custom')

  const destinationContent = allDestinationContent
    ?.filter(c => c.kind === 'Sales')
    ?.filter(c => c.category !== 'Custom')

  const isLoadingCampaigns = useSelector(s => s.campaigns.isLoadingCampaigns)
  const isErred = useSelector(s => s.campaigns.isErred)

  useEffect(() => {
    if (!isLoadingCampaigns && !isErred) {
      if (!originContent) {
        dispatch(getCampaigns(originSubsidiaryId))
      }
      if (!destinationContent) {
        dispatch(getCampaigns(destinationSubsidiaryId))
      }
    }
  }, [
    dispatch,
    originContent,
    originSubsidiaryId,
    isLoadingCampaigns,
    destinationContent,
    destinationSubsidiaryId,
    isErred
  ])

  if (isErred) {
    return (
      <ConfirmationDialog
        negativeMessage="Close"
        onConfirm={handleClose}
        onReject={handleClose}
        title="Can't get campaign details"
      >
        <p>Something went wrong while getting campaign details.</p>
      </ConfirmationDialog>
    )
  }

  if (isLoadingCampaigns || !originContent || !destinationContent) {
    return <LoadingIndicator />
  }

  if (
    originContent &&
    destinationContent &&
    originContent.length !== destinationContent.length
  )
    return (
      <ConfirmationDialog
        negativeMessage="Close"
        onConfirm={handleClose}
        onReject={handleClose}
        title="Different campaign structure"
      >
        <p>Those two hotels have different campaign structure and can't be merged.</p>
      </ConfirmationDialog>
    )

  let differentOriginMessageTemplates = originContent.filter(
    (c, i) => c.messageTemplate !== destinationContent[i].messageTemplate
  )

  const totalNumberOfDifferences = [...differentOriginMessageTemplates].length

  if (totalNumberOfDifferences === 0) {
    return (
      <ConfirmationDialog
        negativeMessage="Close"
        positiveMessage="My bad"
        onConfirm={handleClose}
        onReject={handleClose}
        title="Identical content"
      >
        <p>All campaigns in selected hotels have exact same content.</p>
      </ConfirmationDialog>
    )
  }

  return (
    <ConfirmationDialog
      title={`${totalNumberOfDifferences} campaign${
        totalNumberOfDifferences > 1 ? 's' : ''
      } will be copied`}
      positiveMessage="Overwrite campaigns"
      negativeMessage="Cancel"
      maxWidth={654}
      theme="blacklist"
      onReject={handleClose}
      onConfirm={handleConfirm}
    >
      {isLoadingCampaigns && <LoadingIndicator />}
      {originContent && destinationContent && (
        <div className={styles.wrapper}>
          <div className={styles.row}>
            <div className={styles.origin}>
              <div className={styles.title}>Old copy</div>
              <div className={styles.hotelName}>{originSubsidiaryName}</div>
            </div>
            <div className={styles.destination}>
              <div className={styles.title}>New copy</div>
              <div className={styles.hotelName}>{destinationSubsidiaryName}</div>
            </div>
          </div>
          {differentOriginMessageTemplates.map(c => (
            <div className={styles.row}>
              <div className={styles.origin}>
                <CampaignCard
                  {...originContent.find(campaign => campaign.category === c.category)}
                />
              </div>
              <div className={styles.destination}>
                <CampaignCard
                  {...destinationContent.find(
                    campaign => campaign.category === c.category
                  )}
                />
              </div>
            </div>
          ))}
          <div className={styles.darkBg}></div>
        </div>
      )}
    </ConfirmationDialog>
  )
}

export default CompareCampaigns
